/**
 * ExportFilterDialog - Export with format selection and filters
 * PLATFORM:F1.P9-10 Phase 2: Export filtering (grade range, date range, format, publisher)
 */
import React, { useState } from 'react';
import { Download, X } from 'lucide-react';
import { exportCollection } from '../../services/vaultApi';

const FORMAT_OPTIONS = [
  { value: 'csv', label: 'CSV', description: 'Standard spreadsheet format' },
  { value: 'json', label: 'JSON', description: 'Structured data format' },
  { value: 'clz', label: 'CLZ Compatible', description: 'Import into CLZ Comics app' },
  { value: 'ebay', label: 'eBay Listing', description: 'eBay File Exchange format' },
];

export default function ExportFilterDialog({ isOpen, onClose }) {
  const [format, setFormat] = useState('csv');
  const [gradeMin, setGradeMin] = useState('');
  const [gradeMax, setGradeMax] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [publisher, setPublisher] = useState('');
  const [includeValues, setIncludeValues] = useState(true);
  const [includeGrades, setIncludeGrades] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);

    try {
      const options = {
        format,
        include_values: includeValues,
        include_grades: includeGrades,
        include_notes: includeNotes,
      };

      if (gradeMin) options.grade_min = parseFloat(gradeMin);
      if (gradeMax) options.grade_max = parseFloat(gradeMax);
      if (dateFrom) options.date_from = dateFrom;
      if (dateTo) options.date_to = dateTo;
      if (publisher) options.publisher = publisher;

      // V-EXPORT-FE: exportCollection now returns the FILE ({blob, filename}) -- the old
      // `typeof result === 'string' || result instanceof Blob` guard could never be true
      // (fetchAPI had already tried to JSON-parse the CSV), so the dialog closed with no
      // download and no error. A 400 (e.g. eBay with nothing for sale) is a message the
      // user can act on, and it renders below instead of closing the dialog.
      const { blob, filename } = await exportCollection(options);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      onClose();
    } catch (err) {
      setError(err.message || 'Export failed');
    }
    setIsExporting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="export-filter-dialog">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Download className="w-5 h-5 text-orange-400" />
            <h2 className="text-xl font-bold text-white">Export Collection</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Format Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-zinc-300 mb-2">Format</label>
          <div className="grid grid-cols-2 gap-2">
            {FORMAT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setFormat(opt.value)}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  format === opt.value
                    ? 'border-orange-500 bg-orange-500/10'
                    : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                }`}
                data-testid={`export-format-${opt.value}`}
              >
                <p className={`text-sm font-medium ${format === opt.value ? 'text-orange-400' : 'text-white'}`}>
                  {opt.label}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">{opt.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="space-y-3 mb-4">
          <p className="text-sm font-medium text-zinc-300">Filters (optional)</p>

          {/* Grade range */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs text-zinc-500 mb-1">Min Grade</label>
              <input
                type="number"
                min="0.5"
                max="10"
                step="0.1"
                value={gradeMin}
                onChange={(e) => setGradeMin(e.target.value)}
                placeholder="0.5"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-orange-500 focus:outline-none"
                data-testid="export-grade-min"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-zinc-500 mb-1">Max Grade</label>
              <input
                type="number"
                min="0.5"
                max="10"
                step="0.1"
                value={gradeMax}
                onChange={(e) => setGradeMax(e.target.value)}
                placeholder="10.0"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-orange-500 focus:outline-none"
                data-testid="export-grade-max"
              />
            </div>
          </div>

          {/* Date range */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs text-zinc-500 mb-1">From Date</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:border-orange-500 focus:outline-none"
                data-testid="export-date-from"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-zinc-500 mb-1">To Date</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:border-orange-500 focus:outline-none"
                data-testid="export-date-to"
              />
            </div>
          </div>

          {/* Publisher */}
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Publisher</label>
            <input
              type="text"
              value={publisher}
              onChange={(e) => setPublisher(e.target.value)}
              placeholder="e.g. Marvel, DC"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-orange-500 focus:outline-none"
              data-testid="export-publisher"
            />
          </div>
        </div>

        {/* Include options */}
        <div className="space-y-2 mb-4">
          <p className="text-sm font-medium text-zinc-300">Include</p>
          <label className="flex items-center gap-2 text-sm text-zinc-400">
            <input type="checkbox" checked={includeGrades} onChange={(e) => setIncludeGrades(e.target.checked)} className="rounded" />
            Grading information
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-400">
            <input type="checkbox" checked={includeValues} onChange={(e) => setIncludeValues(e.target.checked)} className="rounded" />
            Values and prices
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-400">
            <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} className="rounded" />
            Notes
          </label>
        </div>

        {error && (
          <p className="text-red-400 text-sm mb-3">{error}</p>
        )}

        <button
          onClick={handleExport}
          disabled={isExporting}
          className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          data-testid="export-confirm-button"
        >
          {/* `format` is only ever set from FORMAT_OPTIONS, so the lookup always resolves */}
          {isExporting ? 'Exporting...' : `Export as ${FORMAT_OPTIONS.find((f) => f.value === format).label}`}
        </button>
      </div>
    </div>
  );
}
